import React, { useEffect, useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Link as MuiLink,
  List,
  ListItem,
  Select,
  Typography,
} from '@mui/material';

export default function TopicNav({ sectionName, topics }) {
  const [activeId, setActiveId] = useState(topics[0]?.id || '');

  useEffect(() => {
    setActiveId(topics[0]?.id || '');
    if (typeof IntersectionObserver === 'undefined' || topics.length < 2) return undefined;
    const observer = new IntersectionObserver(entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((first, second) => first.boundingClientRect.top - second.boundingClientRect.top)[0];
      if (visible) setActiveId(visible.target.id);
    }, { rootMargin: '-15% 0px -70% 0px' });
    topics.forEach(topic => {
      const target = document.getElementById(topic.id);
      if (target) observer.observe(target);
    });
    return () => observer.disconnect();
  }, [topics]);

  if (topics.length < 2) return null;

  const moveTo = id => {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ block: 'start' });
  };

  const labelId = `${sectionName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-topic-select-label`;
  return <>
    <Box component="nav" aria-label={`${sectionName} topics`} sx={{ display: { xs: 'none', md: 'block' } }}>
      <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, mb: 0.5 }}>On this page</Typography>
      <List dense disablePadding>
        {topics.map(topic => <ListItem key={topic.id} disablePadding>
          <MuiLink href={`#${topic.id}`} aria-current={activeId === topic.id ? 'location' : undefined}
            onClick={() => setActiveId(topic.id)}
            sx={{ display: 'block', width: '100%', px: 1.5, py: 1, borderLeft: 3, borderColor: activeId === topic.id ? 'primary.main' : 'transparent',
              bgcolor: activeId === topic.id ? 'primary.light' : 'transparent', fontWeight: activeId === topic.id ? 700 : 400 }}>
            {topic.label}
          </MuiLink>
        </ListItem>)}
      </List>
    </Box>
    <FormControl sx={{ display: { xs: 'block', md: 'none' }, mb: 3, maxWidth: '100%' }}>
      <InputLabel htmlFor={`${labelId}-select`} id={labelId}>On this page</InputLabel>
      <Select native fullWidth id={`${labelId}-select`} labelId={labelId} label="On this page"
        value={activeId} onChange={event => moveTo(event.target.value)}>
        {topics.map(topic => <option key={topic.id} value={topic.id}>{topic.label}</option>)}
      </Select>
    </FormControl>
  </>;
}
